import React from 'react';
import PropTypes from 'prop-types';
import { firestore, } from 'firebaseHelpers';
import { makeStyles } from '@material-ui/styles';
import { Button, Grid, Box, CircularProgress, colors } from '@material-ui/core';
import { useSelector, useDispatch, } from 'react-redux';
import useRouter from 'utils/useRouter'
import { uploadParkingImages, editParking, createParking, deleteParking, getNewParkingId, } from 'firebaseHelpers/parkings'
import { getUserInfosFromEmail, addManagerRoleToUsers, removeManagerRoleToUsers, addGateManagerRoleToUsers, removeGateManagerRoleToUsers, } from 'firebaseHelpers/users'
import {
  UploadImages,
} from 'components'
import { USER, } from 'constants/parkingTypes'
import {
  ParkingInfos,
  ParkingDescription,
  MeansOfAccess,
  AdaptedVehicles,
  Characteristics,
  ParkingManagement,
  ParkingAdmin,
  ParkingInstructions,
} from './components'
import { showSnackbar, } from 'actions'

const useStyles = makeStyles(theme => ({
  actions: {
    marginTop: theme.spacing(3)
  },
  buttonProgress: {
    color: colors.green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  pageIsLoading: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0.5,
    backgroundColor: 'white',
    zIndex: 2,
  },
  buttonIcon: {
    marginRight: theme.spacing(1)
  },
  formContainer: {
    position: 'relative',
  },
}));

const Parking = (props) => {
  const { parkingInfos, ownerEmail, delegateEmail, handleOwnerEmailChange, handleDelegateEmailChange, bkpManagers, files, isCreation, setIsLoading, isLoading, handleFieldChange, handleImagesChange, } = props
  const classes = useStyles();
  const router = useRouter();
  const dispatch = useDispatch();
  const session = useSelector(state => state.session);

  const isAdmin = session.user.roles === 'admin'

  const updateEditedManagers = async (oldManagers, newManagers, parkingId) => {
    const managersToAdd = newManagers.filter((manager) => (!oldManagers.includes(manager)))
    const managersToRemove = oldManagers.filter((manager) => (!newManagers.includes(manager)))

    if (managersToAdd.length) {
      addManagerRoleToUsers(managersToAdd, parkingId)
    }
    if (managersToRemove.length) {
      removeManagerRoleToUsers(managersToRemove, parkingId)
    }
  }

  const updateEditedGateManagers = async (oldGateManagers, newGateManagers, parkingId) => {
    const gateManagersToAdd = newGateManagers.filter((gateManager) => (!oldGateManagers.includes(gateManager)))
    const gateManagersToRemove = oldGateManagers.filter((gateManager) => (!newGateManagers.includes(gateManager)))

    if (gateManagersToAdd.length) {
      addGateManagerRoleToUsers(gateManagersToAdd, parkingId)
    }
    if (gateManagersToRemove.length) {
      removeGateManagerRoleToUsers(gateManagersToRemove, parkingId)
    }
  }

  const saveParking = async (event) => {
    event.preventDefault()
    try {
      const { id, ...parkingInfosFormated } = parkingInfos
      let userParkingData = {
        ejariURL: null,
        ejariURLSmall: null,
        delegate: null,
        delegateCheckin: false,
        owner: null,
      }

      setIsLoading(true)
      if (parkingInfosFormated.type === USER) {
        if (delegateEmail) {
          const delegate = await getUserInfosFromEmail(delegateEmail)

          if (delegate.id === 'null') {
            throw new Error('The email provided for the delegated user is not linked to an account')
          } else {
            userParkingData = {
              ...userParkingData,
             delegate: firestore.collection('users').doc(delegate.id),
             delegateCheckin: true,
           }
          }
        }
        const owner = await getUserInfosFromEmail(ownerEmail)

        if (owner.id === 'null') {
          throw new Error('The email provided for the owner is not linked to an account')
        } else {
          userParkingData = {
            ...userParkingData,
           owner: firestore.collection('users').doc(owner.id),
         }
        }
      }

      const imagesUpdated = await uploadParkingImages(files.images, { isMulti: true, parkingId: parkingInfos.id, })
      const adURLUpdated = await uploadParkingImages(files.adURL, { parkingId: parkingInfos.id })

      if (parkingInfosFormated.type !== USER) {
        const logoUpdated = await uploadParkingImages(files.logo, { parkingId: parkingInfos.id })

        userParkingData = {
          ...userParkingData,
          logo: logoUpdated.url,
          logoSmall: logoUpdated.urlSmall,
          logoName: logoUpdated.names,
        }
      } else if (parkingInfosFormated.type === USER) {
        const ejariURLUpdated = await uploadParkingImages(files.ejariURL, { parkingId: parkingInfos.id })

        userParkingData = {
          ...userParkingData,
          logo: imagesUpdated.url ? imagesUpdated.url[0] : '',
          logoSmall: imagesUpdated.urlSmall ? imagesUpdated.urlSmall[0] : '',
          logoName: imagesUpdated.names ? imagesUpdated.names[0] : null,
          ejariURL: ejariURLUpdated.url,
          ejariURLSmall: ejariURLUpdated.urlSmall,
        }
      }
      const editedParking = {
        ...parkingInfosFormated,
        ...userParkingData,
        nameInsensitive: parkingInfosFormated.name.toLowerCase(),
        images: imagesUpdated.url,
        imagesSmall: imagesUpdated.urlSmall,
        imagesName: imagesUpdated.names,
        adURL: adURLUpdated.url,
        adURLName: adURLUpdated.names,
        autoBooking: parkingInfosFormated.autoBooking === true || parkingInfosFormated.autoBooking === 'true' ? true : false,
        canSetAd: parkingInfosFormated.canSetAd === true || parkingInfosFormated.canSetAd === 'true' ? true : false,
        isAvailable: parkingInfosFormated.isAvailable === true || parkingInfosFormated.isAvailable === 'true' ? true : false,
      }
      await editParking(id, editedParking, parkingInfos.owner, parkingInfos.delegate)
      updateEditedManagers(bkpManagers.managers, editedParking.managers, id)
      updateEditedGateManagers(bkpManagers.gateManagers, editedParking.gateManagers, id)
      setIsLoading(false)
      dispatch(showSnackbar({ variant: 'success', message: 'Parking edited successfully' }))
      router.history.push(`/management/parkings/${parkingInfos.id}`);
    } catch (error) {
      setIsLoading(false)
      dispatch(showSnackbar({ variant: 'error', message: error.message }))
    }
  }

  const handleCreateParking = async (event) => {
    event.preventDefault()
    const { parkingId, latitude, longitude, } = await getNewParkingId(Number(parkingInfos.latitude), Number(parkingInfos.longitude), 0, 1)
    try {
      let userParkingData = {
        ejariURL: null,
        ejariURLSmall: null,
        delegate: null,
        delegateCheckin: false,
        owner: null,
      }
      setIsLoading(true)
      if (parkingInfos.type === USER) {
        if (delegateEmail) {
          const delegate = await getUserInfosFromEmail(delegateEmail)

          if (delegate.id === 'null') {
            throw new Error('The email provided for the delegated user is not linked to an account')
          } else {
            userParkingData = {
              ...userParkingData,
             delegate: firestore.collection('users').doc(delegate.id),
             delegateCheckin: true,
           }
          }
        }
        const owner = await getUserInfosFromEmail(ownerEmail)

        if (owner.id === 'null') {
          throw new Error('The email provided for the owner is not linked to an account')
        } else {
          userParkingData = {
            ...userParkingData,
           owner: firestore.collection('users').doc(owner.id),
         }
        }
      }

      await createParking(parkingId, { ...parkingInfos, latitude, longitude })

      const imagesUpdated = await uploadParkingImages(files.images, { isMulti: true, parkingId: parkingId, })
      const adURLUpdated = await uploadParkingImages(files.adURL, { parkingId: parkingId })

      if (parkingInfos.type !== USER) {
        const logoUpdated = await uploadParkingImages(files.logo, { parkingId: parkingInfos.id })

        userParkingData = {
          ...userParkingData,
          logo: logoUpdated.url,
          logoSmall: logoUpdated.urlSmall,
          logoName: logoUpdated.names,
        }
      } else if (parkingInfos.type === USER) {
        const ejariURLUpdated = await uploadParkingImages(files.ejariURL, { parkingId: parkingInfos.id })

        userParkingData = {
          ...userParkingData,
          logo: imagesUpdated.url.length ? imagesUpdated.url[0] : '',
          logoSmall: imagesUpdated.urlSmall.length ? imagesUpdated.urlSmall[0] : '',
          logoName: imagesUpdated.names.length ? imagesUpdated.names[0] : null,
          ejariURL: ejariURLUpdated.url,
          ejariURLSmall: ejariURLUpdated.urlSmall,
        }
      }

      const editedParking = {
        ...parkingInfos,
        ...userParkingData,
        latitude,
        longitude,
        nameInsensitive: parkingInfos.name.toLowerCase(),
        images: imagesUpdated.url,
        imagesSmall: imagesUpdated.urlSmall,
        imagesName: imagesUpdated.names,
        adURL: adURLUpdated.url,
        adURLName: adURLUpdated.names,
        status: 1,
        price: {
          day: parkingInfos.price.day !== '' ? parkingInfos.price.day : null,
          hour: parkingInfos.price.hour !== '' ? parkingInfos.price.hour : null,
          month: parkingInfos.price.month !== '' ? parkingInfos.price.month : null,
        },
        autoBooking: parkingInfos.autoBooking === true || parkingInfos.autoBooking === 'true' ? true : false,
        canSetAd: parkingInfos.canSetAd === true || parkingInfos.canSetAd === 'true' ? true : false,
        isAvailable: parkingInfos.isAvailable === true || parkingInfos.isAvailable === 'true' ? true : false,
      }

      await editParking(parkingId, editedParking, editedParking.type === USER ? 'create' : null, editedParking.type === USER && editedParking.delegateCheckin ? 'create' : null)
      updateEditedManagers([], editedParking.managers, parkingId)
      updateEditedGateManagers([], editedParking.gateManagers, parkingId)
      setIsLoading(false)
      dispatch(showSnackbar({ variant: 'success', message: 'Parking created successfully' }))
      router.history.push(`/management/parkings/${parkingId}`);
    } catch (error) {
      await deleteParking(parkingId)
      setIsLoading(false)
      dispatch(showSnackbar({ variant: 'error', message: error.message }))
    }
  }

  return (
    <Box>
      <form onSubmit={isCreation ? handleCreateParking : saveParking}>
        <Box className={classes.formContainer}>
          <Box className={isLoading ? classes.pageIsLoading : null}/>
          {isAdmin && <ParkingAdmin parkingInfos={parkingInfos} ownerEmail={ownerEmail} handleFieldChange={handleFieldChange} handleOwnerEmailChange={handleOwnerEmailChange}/>}
          <ParkingInfos parkingInfos={parkingInfos} handleFieldChange={handleFieldChange} isAdmin={isAdmin} isCreation={isCreation}/>
          <ParkingDescription parkingInfos={parkingInfos} handleFieldChange={handleFieldChange}/>
          <ParkingInstructions parkingInfos={parkingInfos} handleFieldChange={handleFieldChange}/>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={6}
              xs={12}
            >
              <MeansOfAccess parkingInfos={parkingInfos} handleFieldChange={handleFieldChange}/>
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <AdaptedVehicles parkingInfos={parkingInfos} handleFieldChange={handleFieldChange}/>
            </Grid>
          </Grid>
          <Characteristics parkingInfos={parkingInfos} handleFieldChange={handleFieldChange}/>
          <ParkingManagement parkingInfos={parkingInfos} delegateEmail={delegateEmail} handleDelegateEmailChange={handleDelegateEmailChange} handleFieldChange={handleFieldChange} isAdmin={isAdmin}/>
          <UploadImages name="Parking Images" files={files.images} handleImagesChange={(images) => { handleImagesChange('images', images)}} isMulti/>
          {parkingInfos.type !== USER && <UploadImages name="Parking Logo" files={files.logo} handleImagesChange={(images) => { handleImagesChange('logo', images)}}/>}
          {parkingInfos.type === USER && <UploadImages name="Ejari" files={files.ejariURL} handleImagesChange={(images) => { handleImagesChange('ejariURL', images)}}/>}
          {parkingInfos.canSetAd && <UploadImages name="Ad" files={files.adURL} handleImagesChange={(images) => { handleImagesChange('adURL', images)}}/>}
        </Box>
        <div className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            disabled={isLoading}
          >
            {isCreation ? 'Create parking': 'Save parking' }
            {isLoading && <CircularProgress size={24} className={classes.buttonProgress} />}
          </Button>
        </div>
      </form>
    </Box>
  );
};

Parking.propTypes = {
  ownerEmail: PropTypes.string,
  delegateEmail: PropTypes.string,
  isCreation: PropTypes.bool,
  isLoading: PropTypes.bool,
  parkingInfos: PropTypes.object,
  bkpManagers: PropTypes.object,
  files: PropTypes.object,
  handleImagesChange: PropTypes.func,
  handleFieldChange: PropTypes.func,
  setIsLoading: PropTypes.func,
  handleDelegateEmailChange: PropTypes.func,
  handleOwnerEmailChange: PropTypes.func,
};

Parking.defaultProps = {
  isCreation: false,
};

export default Parking;
